import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Grid,
  TextField,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/api"
    : "https://artikaorder-backend.onrender.com/api";

const STAGES = [
  { key: "mockup", label: "🖌️ صور الموكاب", color: "#c0392b" },
  { key: "installationPreview", label: "🧱 صور التركيب (عند الطلب)", color: "#d35400" },
  { key: "drawing", label: "🎨 فيديوات الرسم", color: "#8e44ad" },
  { key: "compareBoards", label: "🖼️ صور مقارنة اللوحات", color: "#f39c12" },
  { key: "framing", label: "🖼️ فيديوات التأطير", color: "#2980b9" },
  { key: "packaging", label: "📦 فيديوات التغليف", color: "#16a085" },
  { key: "installation", label: "🛠️ صور وفيديوهات التركيب", color: "#e67e22" },
  { key: "feedback", label: "💬 صور وفيديوهات الفيدباك", color: "#27ae60" },
];

const ARABIC_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

// ترتيب حقول مقارنة اللوحات (نفس ترتيب COMPARE_BOARDS_FIELDS بالباك إند) -
// نستخدمه لترتيب صور نفس اللوحة وحدة جنب الثانية داخل كل طلب
const COMPARE_BOARDS_FIELD_ORDER = [
  "sentToCustomer",
  "printedBoard",
  "halimaPrintedBoard",
  "drawingBoard",
];

// بطاقة صورة/فيديو وحدة - مشتركة بين عرض الشبكة العادي وعرض مقارنة اللوحات
// المجمّع حسب الطلب، حتى ما نكرر نفس الكود بالمكانين
function StageItemThumb({ item, stageColor, onOpen }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        transition: "0.2s",
        "&:hover": { bgcolor: "#f1f3f4", borderColor: "#d0d0d0" },
      }}
    >
      <Box
        onClick={() => onOpen(item)}
        sx={{
          width: "100%",
          aspectRatio: "1/1",
          bgcolor: stageColor,
          borderRadius: 1,
          overflow: "hidden",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: item.mediaType === "image" ? `url(${item.url})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {item.mediaType !== "image" && (
          <PlayCircleOutlineIcon sx={{ fontSize: 40, color: "#fff", opacity: 0.9 }} />
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          fontWeight: 500,
          fontSize: "12px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "right",
          color: "#3c4043",
        }}
      >
        {item.customerName}
      </Typography>
      {item.label && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: "11px",
            textAlign: "right",
            color: "#7a7a7a",
          }}
        >
          {item.label}
        </Typography>
      )}
    </Paper>
  );
}

const AllStageVideosDashboard = () => {
  const [stageData, setStageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [activeStage, setActiveStage] = useState(0);
  const [previewItem, setPreviewItem] = useState(null);
  const username = localStorage.getItem("username");

  const allowedUsers = ["admin", "Baneen@123", "designer@123"];

  useEffect(() => {
    if (!allowedUsers.includes(username)) return;

    const fetchStageVideos = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await fetch(`${API_BASE}/admin-dashboard/stage-videos`);
        if (!res.ok) throw new Error("bad response");
        const json = await res.json();
        setStageData(json.stages || {});
      } catch (error) {
        console.error("❌ خطأ في جلب فيديوات الطلبات:", error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStageVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stageKey = STAGES[activeStage].key;
  const stageColor = STAGES[activeStage].color;

  const monthGroups = useMemo(() => {
    const monthsForStage = (stageData && stageData[stageKey]) || {};
    if (!search) return monthsForStage;

    const term = search.toLowerCase();
    const filtered = {};
    Object.entries(monthsForStage).forEach(([monthKey, items]) => {
      const matched = items.filter((item) =>
        (item.customerName || "").toLowerCase().includes(term)
      );
      if (matched.length) filtered[monthKey] = matched;
    });
    return filtered;
  }, [stageData, stageKey, search]);

  const sortedMonthKeys = useMemo(
    () => Object.keys(monthGroups).sort((a, b) => b.localeCompare(a)),
    [monthGroups]
  );

  // 🖼️ لصور مقارنة اللوحات تحديداً: نجمع صور نفس الطلب سوا (بدل ما تجي
  // متفرقة بنفس الشبكة)، وداخل الطلب نرتب صور نفس اللوحة وحدة جنب الثانية
  // (المرسلة/المطبوعة/المطبوعة عند حليمة/المرسومة) حتى تسهل المقارنة
  const compareBoardsOrderGroups = useMemo(() => {
    if (stageKey !== "compareBoards") return null;
    const result = {};
    Object.entries(monthGroups).forEach(([monthKey, items]) => {
      const byOrder = {};
      items.forEach((item) => {
        const key = item.orderId || item.customerName;
        if (!byOrder[key]) {
          byOrder[key] = { orderId: key, customerName: item.customerName, items: [] };
        }
        byOrder[key].items.push(item);
      });
      Object.values(byOrder).forEach((group) => {
        group.items.sort((a, b) => {
          const boardDiff = (a.boardIndex ?? 0) - (b.boardIndex ?? 0);
          if (boardDiff !== 0) return boardDiff;
          return (
            COMPARE_BOARDS_FIELD_ORDER.indexOf(a.field) -
            COMPARE_BOARDS_FIELD_ORDER.indexOf(b.field)
          );
        });
      });
      result[monthKey] = Object.values(byOrder);
    });
    return result;
  }, [monthGroups, stageKey]);

  const formatMonthLabel = (monthKey) => {
    if (monthKey === "بدون تاريخ") return monthKey;
    const [year, month] = monthKey.split("-");
    const monthName = ARABIC_MONTHS[Number(month) - 1] || month;
    return `${monthName} ${year}`;
  };

  if (!allowedUsers.includes(username)) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">ليس لديك صلاحية لعرض هذه الصفحة</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }} dir="rtl">
      <Typography
        variant="h5"
        sx={{ fontWeight: "bold", mb: 3, textAlign: "right", color: "#333" }}
      >
        🎬 فيديوات كل المراحل
      </Typography>

      <Tabs
        value={activeStage}
        onChange={(e, v) => setActiveStage(v)}
        variant="fullWidth"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {STAGES.map((stage, i) => (
          <Tab key={stage.key} label={stage.label} value={i} />
        ))}
      </Tabs>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="بحث باسم الزبون..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ bgcolor: "#fff", borderRadius: 1 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error">تعذّر تحميل الفيديوهات، حاول مرة أخرى.</Alert>
      ) : sortedMonthKeys.length === 0 ? (
        <Alert severity="info">لا توجد فيديوهات لهذه المرحلة.</Alert>
      ) : (
        sortedMonthKeys.map((monthKey, index) => (
          <Accordion
            key={monthKey}
            defaultExpanded={index === 0}
            TransitionProps={{ unmountOnExit: true }}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">
                📅 {formatMonthLabel(monthKey)} — ({monthGroups[monthKey].length}{" "}
                {stageKey === "installation"
                  ? "ملف"
                  : stageKey === "mockup" || stageKey === "compareBoards"
                  ? "صورة"
                  : "فيديو"}
                )
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {stageKey === "compareBoards" ? (
                <Box display="flex" flexDirection="column" gap={2.5}>
                  {compareBoardsOrderGroups[monthKey].map((group) => (
                    <Box key={group.orderId}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1, color: "#555" }}
                      >
                        👤 {group.customerName}
                      </Typography>
                      <Grid container spacing={1.5}>
                        {group.items.map((item) => (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={item.id}>
                            <StageItemThumb item={item} stageColor={stageColor} onOpen={setPreviewItem} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Grid container spacing={1.5}>
                  {monthGroups[monthKey].map((item) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={item.id}>
                      <StageItemThumb item={item} stageColor={stageColor} onOpen={setPreviewItem} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: "#000", position: "relative" }}>
          <IconButton
            onClick={() => setPreviewItem(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewItem && previewItem.mediaType === "image" ? (
            <img
              key={previewItem.url}
              src={previewItem.url}
              alt={previewItem.customerName}
              style={{ width: "100%", maxHeight: "80vh", display: "block", objectFit: "contain" }}
            />
          ) : previewItem ? (
            <video
              key={previewItem.url}
              src={previewItem.url}
              controls
              autoPlay
              style={{ width: "100%", maxHeight: "80vh", display: "block" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default AllStageVideosDashboard;
